import type { NetworkSignals } from "../types";
import { withTimeout } from "../utils/safe";

const IPV4 = /(\d{1,3}(?:\.\d{1,3}){3})/;
const IPV6 = /([a-f0-9]{1,4}(?::[a-f0-9]{1,4}){7}|(?:[a-f0-9]{1,4}:){1,7}:)/i;

function isPrivate(ip: string): boolean {
  // RFC1918 + loopback + link-local + mDNS .local + IPv6 ULA/link-local.
  return (
    /^10\./.test(ip) ||
    /^192\.168\./.test(ip) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(ip) ||
    /^169\.254\./.test(ip) ||
    /^127\./.test(ip) ||
    ip.endsWith(".local") ||
    /^(fe80|fc|fd)/i.test(ip)
  );
}

/**
 * Gather WebRTC candidate IPs. Local IPs reveal NAT/VPN/proxy layering; a public
 * IP from STUN can disagree with the HTTP-observed IP (proxy tell). mDNS obfuscation
 * (`*.local`) in modern Chrome means local IPs are often hidden — that's expected.
 */
export function collectWebrtc(timeoutMs: number): Promise<NetworkSignals> {
  const empty: NetworkSignals = { webrtcPublicIp: null, webrtcLocalIps: [] };

  const RTC = (window as Window & {
    RTCPeerConnection?: typeof RTCPeerConnection;
    webkitRTCPeerConnection?: typeof RTCPeerConnection;
  }).RTCPeerConnection ?? (window as any).webkitRTCPeerConnection;
  if (!RTC) return Promise.resolve(empty);

  const run = new Promise<NetworkSignals>((resolve) => {
    const local = new Set<string>();
    let publicIp: string | null = null;
    let pc: RTCPeerConnection | null = null;

    const finish = () => {
      try {
        pc?.close();
      } catch {
        /* noop */
      }
      resolve({ webrtcPublicIp: publicIp, webrtcLocalIps: [...local] });
    };

    try {
      const conn: RTCPeerConnection = new RTC({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      pc = conn;
      conn.createDataChannel("fp");
      conn.onicecandidate = (e) => {
        if (!e.candidate) {
          finish();
          return;
        }
        const cand = e.candidate.candidate;
        const m = cand.match(IPV4) || cand.match(IPV6);
        if (!m) return;
        const ip = m[1]!;
        // `typ srflx` candidates carry the server-reflexive (public) IP.
        if (/typ srflx/.test(cand) && !isPrivate(ip)) publicIp = ip;
        else if (isPrivate(ip)) local.add(ip);
        else publicIp = publicIp ?? ip;
      };
      conn.createOffer().then(
        (offer) => conn.setLocalDescription(offer),
        () => finish()
      );
    } catch {
      finish();
    }
  });

  return withTimeout(run, timeoutMs, empty);
}
